import { ChevronDown, ChevronUp, UtensilsCrossed } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { usePostProcessGateRecipeData } from "../../../api/process";
import { useGetSuppliersByIds } from "../../../api/supplier";
import { useGetProcessRecipeByProcessId } from "../../../api/supplierProcessGate";
import { Card, CardContent, CardHeader } from "../../../components/ui/common/card";
import { AccordionTypes } from "../../../helpers/AccordionHelper";
import { successMessage } from "../../../helpers/ErrorHandler";
import { RecipeFormActions } from "./components/RecipeFormActions";
import { RecipeIngredientTable } from "./components/RecipeIngredientTable";
import { RecipeProcessTable } from "./components/RecipeProcessTable";
import { useRecipeData } from "./hooks/useRecipeData";
import { useRecipeFormValues } from "./hooks/useRecipeFormValues";
import { useRecipeValidation } from "./hooks/useRecipeValidation";
import { extractSupplierIds, getSupplierName } from "./utils/supplierUtils";

interface RecipeContentProps {
  processId: string;
  recipeId: string;
  nodeItem?: any;
  type: string;
  accordionType: AccordionTypes | null;
  refetchProcess: () => void;
  lockStatus?: boolean;
  handleAccordion: (requestType: AccordionTypes) => void;
  onRecipeSave?: (processId: string) => void;
  disabled?: boolean;
}

const RecipeContent = ({
  processId,
  recipeId,
  accordionType,
  refetchProcess,
  nodeItem,
  type,
  lockStatus = false,
  handleAccordion,
  onRecipeSave = (processId: string) => {},
  disabled = false
}: RecipeContentProps) => {
  const accordionEnabled = accordionType === AccordionTypes.RECIPE;
  const [open, setOpen] = useState(accordionEnabled);
  const [supplierIds, setSupplierIds] = useState<string[]>([]);

  // Fetch process recipe data
  const { data: processRecipeData } = useGetProcessRecipeByProcessId({
    recipeId: recipeId,
    type: type
  });

  // Custom hooks for separated concerns
  const { recipeData, loadRecipeData } = useRecipeData({
    processId,
    nodeItem,
    processRecipeData,
    accordionType
  });

  const values = useRecipeFormValues({
    recipeData,
    nodeItem,
    processId,
    recipeId,
    type,
    accordionEnabled
  });

  const { handleSubmit, getValues, control } = useForm({
    mode: "all",
    defaultValues: values,
    values: values
  });

  const {
    validationError,
    validateTotalValue,
    validateRecipeValuePercent,
    isFormValid,
    getErrorMessage
  } = useRecipeValidation(getValues);

  const { mutate: postProcessGateRecipeData } = usePostProcessGateRecipeData();
  const suppliers = useGetSuppliersByIds(supplierIds);

  // Submit handler
  const onSubmitHandler = (data: any, initialSubmit = false) => {
    const formData: any = getValues();
    let processList = [];
    let ingredientList = [];

    for (const key in formData) {
      if (key.includes("process") && key.split("-")[1]) {
        const processNode = values.recipeData?.processList?.find(
          (process: any) => process.data?.reference?.processId === key.split("-")[1]
        );
        processList.push({
          id: key.split("-")[1],
          value: formData[key].value,
          parentNodeType: processNode?.parentNodeDetails?.type,
          parentNodeId:
            processNode?.parentNodeDetails?.processGateId ||
            processNode?.parentNodeDetails?.farmGateId,
          previousComponentId: processNode?.previousComponentId,
          previousComponentType: processNode?.previousComponentType
        });
      } else if (key.includes("ingredient") && key.split("-")[2]) {
        const ingredientNode = values.recipeData?.ingredientList?.find(
          (ingredient: any) => ingredient.data?.reference?.ingredientId === key.split("-")[2]
        );
        ingredientList.push({
          id: key.split("-")[2],
          value: formData[key].value,
          supplierId: key.split("-")[1],
          previousComponentId: ingredientNode?.previousComponentId,
          previousComponentType: ingredientNode?.previousComponentType
        });
      }
    }

    const recipeDataToSubmit = {
      processId,
      processList,
      ingredientList,
      type
    };

    postProcessGateRecipeData(recipeDataToSubmit, {
      onSuccess: () => {
        successMessage("Recipe Added Successfully");
        onRecipeSave(processId);
        if (!initialSubmit) {
          handleAccordion(AccordionTypes.RECIPE);
        }
        refetchProcess();
      }
    });
  };

  // Load recipe data when accordion opens
  useEffect(() => {
    if (accordionType === AccordionTypes.RECIPE) {
      loadRecipeData();
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [accordionType, processRecipeData]);

  // Update supplier IDs when ingredient list changes
  useEffect(() => {
    if (values.recipeData?.ingredientList?.length > 0) {
      const ids = extractSupplierIds(values.recipeData.ingredientList);
      setSupplierIds(ids);
    }

    // Auto-submit for single item
    if (
      (values.recipeData?.processList?.length === 1 ||
        values.recipeData?.ingredientList?.length === 1) &&
      accordionType === AccordionTypes.RECIPE &&
      !processRecipeData
    ) {
      handleSubmit(data => onSubmitHandler(data, true))();
    }
  }, [values]);

  const handleClick = () => {
    handleAccordion(AccordionTypes.RECIPE);
  };

  return (
    <Card className={`tw-border-border ${disabled ? "tw-opacity-50 tw-pointer-events-none" : ""} tw-p-0 tw-gap-0`}>
      <CardHeader
        className="tw-bg-secondary/50 tw-py-3.5 tw-px-3.5 tw-rounded-t-lg tw-cursor-pointer"
        onClick={handleClick}
      >
        <div className="tw-flex tw-items-center tw-justify-between tw-w-full">
          <div className="tw-flex tw-items-center tw-gap-2">
            <UtensilsCrossed className="tw-h-4 tw-w-4 tw-text-primary" />
            <span className="tw-text-sm tw-font-semibold tw-text-foreground">Recipe</span>
          </div>
          <div className="tw-flex tw-items-center tw-gap-2">
            {open ? (
              <ChevronUp className="tw-h-4 tw-w-4 tw-text-muted-foreground" />
            ) : (
              <ChevronDown className="tw-h-4 tw-w-4 tw-text-muted-foreground" />
            )}
          </div>
        </div>
      </CardHeader>

      {open && (
        <CardContent className="tw-py-3.5 tw-px-3.5 tw-space-y-4">
          <form onSubmit={handleSubmit(data => onSubmitHandler(data))} className="tw-space-y-6">
            {/* Process Table */}
            <RecipeProcessTable
              processList={values.recipeData?.processList || []}
              control={control}
              validateTotalValue={validateTotalValue}
              disabled={lockStatus}
            />

            {/* Ingredient Table */}
            <RecipeIngredientTable
              ingredientList={values.recipeData?.ingredientList || []}
              control={control}
              validateTotalValue={validateTotalValue}
              validateRecipeValuePercent={validateRecipeValuePercent}
              validationError={validationError}
              getSupplierName={getSupplierName}
              suppliers={suppliers}
              disabled={lockStatus}
            />

            {/* Form Actions and Error Messages */}
            <RecipeFormActions
              hasError={!isFormValid()}
              errorMessage={getErrorMessage()}
              validationErrorExists={Boolean(validationError.error)}
              onCancel={() => setOpen(false)}
              onSave={handleSubmit(data => onSubmitHandler(data))}
              isSaveDisabled={!isFormValid() || lockStatus}
            />
          </form>
        </CardContent>
      )}
    </Card>
  );
};

export default RecipeContent;
